import { baseApi } from "../baseApi";
import { 
  ConversationReportResponse, 
  ConversationReportQuery,
  ChannelDistributionReportResponse,
  ChannelDistributionReportQuery,
  AgentHistoricalOccupancyResponse,
  AgentHistoricalOccupancyQuery,
  TicketAnalysisReportResponse,
  TicketAnalysisReportQuery,
  KBIssueInsightsResponse,
  KBIssueInsightsQuery,
  AiCostSessionsResponse,
  AiCostSessionsQuery,
  AiCostCumulativeResponse,
  AiCostCumulativeQuery
} from "../../types/reports";

const reportsTag = "Reports";

export const reportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getClientConversationReport: builder.query<ConversationReportResponse, ConversationReportQuery>({
      query: (params) => ({
        url: `/reports/client-conversations`,
        params,
      }),
      providesTags: [reportsTag],
    }),
    
    getChannelDistributionReport: builder.query<ChannelDistributionReportResponse, ChannelDistributionReportQuery>({
      query: (params) => ({
        url: `/reports/channel-distribution`,
        params,
      }),
      // Backend returns { sessions, ai_outcomes, tickets, rates }; the dashboard
      // and the typed contract use { summary, percentages }. Map here so callers
      // don't have to know about the underlying field names.
      transformResponse: (raw: any): ChannelDistributionReportResponse => ({
        ok: raw?.ok ?? false,
        period: raw?.period ?? { start_date: "", end_date: "" },
        summary: {
          total_widget_sessions: raw?.sessions?.total ?? 0,
          resolved_by_ai_agent: raw?.ai_outcomes?.truly_resolved ?? 0,
          resolved_by_ticket: raw?.sessions?.produced_ticket ?? 0,
          transferred_to_live_agent: raw?.sessions?.escalated_to_live_agent ?? 0,
          tickets_from_mail: raw?.tickets?.from_mail ?? 0,
        },
        percentages: {
          ai_agent_rate: raw?.rates?.ai_resolution_rate ?? 0,
          ticket_rate: raw?.rates?.ticket_creation_rate ?? 0,
          live_agent_rate: raw?.rates?.escalation_rate ?? 0,
        },
      }),
      providesTags: [reportsTag],
    }),

    getAgentHistoricalOccupancyReport: builder.query<AgentHistoricalOccupancyResponse, AgentHistoricalOccupancyQuery>({
      query: (params) => ({
        url: `/reports/agent-historical-occupancy`,
        params,
      }),
      providesTags: [reportsTag],
    }),
    
    downloadClientConversationReport: builder.mutation<Blob, ConversationReportQuery>({
      query: (params) => ({
        url: `/reports/client-conversations/download`,
        params,
        responseHandler: (response) => response.blob(),
      }),
    }),

    getTicketAnalysisReport: builder.query<TicketAnalysisReportResponse, TicketAnalysisReportQuery>({
      query: (params) => ({
        url: `/gw/tickets/report`,
        params,
      }),
      providesTags: [reportsTag],
    }),


    downloadTicketAnalysisReport: builder.mutation<
      { data: Blob },
      TicketAnalysisReportQuery
    >({
      query: (params) => ({
        url: `/gw/tickets/report/pdf`,
        params,
        responseHandler: async (response) => {
          if (!response.ok) {
            const text = await response.text();
            throw new Error(text);
          }
          const blob = await response.blob();
          return { data: blob };
        },
        cache: "no-cache",
      }),
      transformResponse: (response: { data: Blob }) =>  response,
    }),

    getKBIssueInsights: builder.query<KBIssueInsightsResponse, KBIssueInsightsQuery>({
      query: (params) => ({
        url: `/reports/kb-issue-insights`,
        params,
      }),
      providesTags: [reportsTag],
    }),

    downloadKBIssueInsightsReport: builder.mutation<Blob, KBIssueInsightsQuery>({
      query: (params) => ({
        url: `/reports/kb-issue-insights/download`,
        params,
        responseHandler: (response) => response.blob(),
      }),
    }),

    downloadKBUnmatchedQueriesReport: builder.mutation<Blob, KBIssueInsightsQuery>({
      query: (params) => ({
        url: `/reports/kb-issue-insights/unmatched-queries/download`,
        params,
        responseHandler: (response) => response.blob(),
      }),
    }),

    getAiCostSessions: builder.query<AiCostSessionsResponse, AiCostSessionsQuery>({
      query: (params) => ({
        url: `/reports/ai-cost/sessions`,
        params,
      }),
      providesTags: [reportsTag],
    }),

    getAiCostCumulative: builder.query<AiCostCumulativeResponse, AiCostCumulativeQuery | void>({
      query: (params) => ({
        url: `/reports/ai-cost/cumulative`,
        params: params || undefined,
      }),
      providesTags: [reportsTag],
    }),
  }),
});

export const { 
  useGetClientConversationReportQuery, 
  useLazyGetClientConversationReportQuery,
  useDownloadClientConversationReportMutation,
  useGetChannelDistributionReportQuery,
  useLazyGetChannelDistributionReportQuery,
  useGetAgentHistoricalOccupancyReportQuery,
  useLazyGetAgentHistoricalOccupancyReportQuery,
  useGetTicketAnalysisReportQuery,
  useLazyGetTicketAnalysisReportQuery,
  useDownloadTicketAnalysisReportMutation,
  useGetKBIssueInsightsQuery,
  useLazyGetKBIssueInsightsQuery,
  useDownloadKBIssueInsightsReportMutation,
  useDownloadKBUnmatchedQueriesReportMutation,
  useGetAiCostSessionsQuery,
  useLazyGetAiCostSessionsQuery,
  useGetAiCostCumulativeQuery,
  useLazyGetAiCostCumulativeQuery,
} = reportsApi;
